import {
  Component,
  EventEmitter,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormControlDirective,
  FormControlName,
  FormControlStatus,
  FormGroupDirective,
  NgControl,
  Validators,
} from '@angular/forms';
import { Subscription } from 'rxjs';
import * as uuid from 'uuid';
import { defaultFormValidationError } from './eo-base-form-control.config';
import {
  FormControlConfig,
  IFormComponent,
  IFormControlConfig,
  IFormValidationError,
} from './eo-base-form-control.model';

export type EoFormControlType = '';

@Component({
  selector: 'eo-base-form-control',
  templateUrl: './eo-base-form-control.component.html',
  styleUrl: './eo-base-form-control.component.scss',
})
export class EoBaseFormControlComponent
  implements OnInit, OnDestroy, IFormComponent, ControlValueAccessor
{
  public subscriptions: Subscription[] = [];
  private previousStatus?: FormControlStatus;
  // TODO: add a FormConfig Input that inits the data of the form

  id: string;
  formControl!: FormControl<any>;
  locale: any;

  @Input() hidden = false;
  @Input() tabIndex = 1;
  @Input() styleClass?: string;
  @Input() label?: string;
  @Input() help?: string;
  @Input() language?: string;
  @Output() statusChange = new EventEmitter<FormControlStatus>();
  public loading = false;

  _errors?: IFormValidationError;
  @Input() set errors(val: IFormValidationError | undefined) {
    if (!val) {
      this._errors = defaultFormValidationError;
      return;
    }
    this._errors = { ...this._errors, ...val };
  }
  get errors() {
    return this._errors;
  }

  protected _config?: FormControlConfig;
  @Input() set config(val: IFormControlConfig | undefined) {
    this._config = undefined;
    if (!val) return;
    this.config = val;
    this.injectFormControl;
  }
  get config() {
    return this._config;
  }

  @Output() change: EventEmitter<any> = new EventEmitter();
  @Output() blur: EventEmitter<any> = new EventEmitter();
  @Output() focus: EventEmitter<any> = new EventEmitter();

  constructor(private injector?: Injector) {
    this.id = uuid.v4();
  }

  ngOnInit() {
    this.injectFormControl();
    this.subscriptions = [this.getStatusChangesSub$()];
  }
  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s?.unsubscribe());
  }

  private injectFormControl() {
    if (!this.injector) return;

    const ngControl = this.injector.get(NgControl);

    if (ngControl instanceof FormControlName) {
      this.formControl = this.injector
        .get(FormGroupDirective)
        .getControl(ngControl);
    } else {
      this.formControl = (ngControl as FormControlDirective)
        .form as FormControl;
    }
  }

  public validate(): boolean {
    return this.formControl?.valid ?? false;
  }

  isRequired(): boolean {
    return this.formControl?.hasValidator(Validators.required) ?? false;
  }

  // implement ControlValueAccessor start ------------------
  writeValue(obj: any): void {}
  registerOnChange(fn: any): void {}
  registerOnTouched(fn: any): void {}
  // implement ControlValueAccessor end ------------------

  upsert(arr: any[], newArr: any[], prop: string) {
    newArr.forEach((e) => {
      arr = [...arr.filter((obj) => obj[prop] !== e[prop]), { ...e }];
    });
    return arr;
  }

  private getStatusChangesSub$(): Subscription {
    return this.formControl?.statusChanges?.subscribe((status) => {
      if (this.previousStatus != status) {
        this.previousStatus = status;
        this.statusChange.emit(status);
      }
    });
  }
  protected getStyleClass(): string | undefined {
    return this.styleClass ?? this.config?.styleClass;
  }
  protected getTabIndex(): number {
    return this.tabIndex ?? this.config?.tabIndex;
  }
  protected getLabel(): string {
    return this.label ?? this.config?.label ?? '';
  }
  protected getHelp(): string {
    return this.help ?? this.config?.help ?? '';
  }
  protected getFormControl(): FormControl<any> {
    return this.formControl ?? this.config!.control;
  }
  protected getErrors(): IFormValidationError | undefined {
    return {
      ...defaultFormValidationError,
      ...this.config?.errors,
      ...this.errors,
    };
  }
  protected isLoading(): boolean {
    return this.loading;
  }
  protected getConfig(): IFormControlConfig | undefined {
    return this.config;
  }

  protected isVisible():boolean{
    return !this.hidden;
  }
}
